<template>
  <div :class="['border-round-2xl', modalClass]">
    <Dialog
      :visible.sync="opened"
      :modal="modal"
      :dismissableMask="dismissableMask"
      :closable="closable"
      :breakpoints="breakpoints"
      :containerStyle="containerStyle"
      :contentStyle="styles"
      :draggable="false"
      @hide="onHide"
    >
      <!-- ICON -->
      <div v-if="showIcon" class="w-full flex justify-content-center mb-3">
        <!-- slot opcional para icono; si no se provee, usa el default por type -->
        <slot name="icon">
          <component :is="resolvedIcon" :size="iconSize" :color="resolvedColors.icon" />
        </slot>
      </div>

      <!-- TITLE -->
      <div class="w-full text-center mb-2">
        <slot name="title">
          <h3 class="m-0 title primaryFont">
            {{ title }}
          </h3>
        </slot>
      </div>

      <!-- SEPARATOR -->
      <div class="w-full flex justify-content-center mb-3">
        <span class="separator" :style="{ backgroundColor: resolvedColors.brand }"></span>
      </div>

      <!-- MESSAGE / DEFAULT SLOT -->
      <div class="w-full text-center">
        <slot>
          <p class="m-0 message primaryFont">{{ message }}</p>
        </slot>
      </div>

      <!-- ACTION AREA (opcional) -->
      <div v-if="hasActionSlot" class="w-full mt-3">
        <slot
          name="action"
          :set-valid="setActionValid"
          :valid="actionValid"
          :append-to="appendTo"
        ></slot>
      </div>

      <!-- FOOTER -->
      <template #footer>
        <div v-if="showFooter" class="w-full flex justify-content-center gap-3 pt-2">
          <slot name="footer">
            <!-- Negativo (izq) outlined -->
            <Button
              :label="buttonLabels[1]"
              class="p-button-outlined button"
              :disabled="loading"
              :style="outlinedStyle"
              @click="handleCancel"
              v-if="showSecondaryButton"
            />
            <!-- Positivo (der) filled -->
            <Button
              :label="buttonLabels[0]"
              :loading="loading"
              :disabled="loading || (requireAction && !actionValid)"
              :style="filledStyle"
              @click="handleConfirm"
              class="button"
            />
          </slot>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, useSlots, computed } from "vue";
import { UserRoundCheck, TriangleAlert, CircleX, Info, Trash, CircleCheckBig } from "lucide-vue";

const emit = defineEmits(["confirm", "cancel", "close"]);

const slots = useSlots();
const hasActionSlot = computed(() => !!slots.action);
const actionValid = ref(!props.requireAction);
const setActionValid = (v) => (actionValid.value = !!v);

const props = defineProps({
  /** Apariencia y semántica */
  modalType: { type: String, default: "success" }, // success | warning | error | info
  title: { type: String, default: "" },
  message: { type: String, default: "" },
  showIcon: { type: Boolean, default: true },
  iconSize: { type: [Number, String], default: 60 },

  /** Comportamiento */
  closable: { type: Boolean, default: true },
  modal: { type: Boolean, default: true },
  dismissableMask: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },

  /** Botones: [positivo, negativo] */
  buttonLabels: {
    type: Array,
    default: () => ["Aceptar", "Cancelar"],
  },
  showFooter: { type: Boolean, default: true },
  showSecondaryButton: { type: Boolean, default: true },
  requireAction: { type: Boolean, default: false },

  /** Layout */
  width: { type: [Number, String], default: 520 },
  maxWidth: { type: [Number, String], default: 640 },
  modalClass: { type: String, default: "" },
  styles: { type: Object, default: () => ({ width: "500px" }) },
  breakpoints: {
    type: Object,
    default: () => ({ "960px": "90vw", "640px": "95vw" }),
  },
  /** Dropdown behavior */
  appendTo: { type: String, default: "body" },
});

const opened = ref(false);
const open = () => (opened.value = true);
const close = () => (opened.value = false);

defineExpose({ open, close });

const palette = {
  success: { brand: "#268C2D", icon: "#268C2D", filledText: "#FFFFFF" },
  warning: { brand: "#FFB700", icon: "#FFB700", filledText: "#FFFFFF" },
  error: { brand: "#DC2626", icon: "#DC2626", filledText: "#FFFFFF" },
  delete: { brand: "#DC2626", icon: "#DC2626", filledText: "#FFFFFF" },
  info: { brand: "#0B5AA2", icon: "#0B5AA2", filledText: "#FFFFFF" },
};

const resolvedColors = computed(() => palette[props.modalType] || palette.success);

const iconByType = {
  success: CircleCheckBig,
  warning: TriangleAlert,
  error: CircleX,
  info: Info,
  delete: Trash,
};
const resolvedIcon = computed(() => iconByType[props.modalType] || CircleCheckBig);

const containerStyle = computed(() => ({
  borderRadius: "16px",
  padding: "16px",
}));

const filledStyle = computed(() => ({
  backgroundColor: resolvedColors.value.brand,
  borderColor: resolvedColors.value.brand,
  color: resolvedColors.value.filledText,
}));

const outlinedStyle = computed(() => ({
  color: resolvedColors.value.brand,
  borderColor: resolvedColors.value.brand,
}));

/* =========================
 * Handlers
 * ========================= */
const handleConfirm = () => emit("confirm");
const handleCancel = () => emit("cancel");
const onHide = () => emit("close");
</script>

<style scoped>
.title {
  color: #111928;
  font-size: 24px;
  font-weight: 500;
}

.message {
  color: #111928;
  font-size: 14px;
  font-weight: 400;
}

.separator {
  display: inline-block;
  width: 88px;
  height: 3px;
  border-radius: 9999px;
}

.button {
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  min-width: 130px;
  min-height: 43px;
  transition: opacity 0.1s ease-in-out;
}

.button:hover {
  opacity: 0.7;
}

.p-dialog {
  transform-origin: center !important;
}

.p-dialog-enter-active,
.p-dialog-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.p-dialog-enter,
.p-dialog-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

:deep(.p-dialog .p-dialog-header) {
  justify-content: end;
}

:deep(.p-dialog .p-dialog-content) {
  overflow: visible !important;
}

:deep(.p-dialog .p-dialog) {
  overflow: visible !important;
}

:deep(.p-dialog .p-autocomplete-panel),
:deep(.p-dialog .p-dropdown-panel),
:deep(.p-dialog .p-multiselect-panel) {
  z-index: 9999 !important;
  position: fixed !important;
  min-width: 100% !important;
  width: max-content !important;
}
</style>
